import { EdgeItemType } from 'data/types';
import {
  ComputeOpeningDragPlacementInput,
  OpeningDoorKindType,
  OpeningDragType,
  OpeningGarageSizeType,
  OpeningPresetType,
  OpeningWindowSizeType
} from 'utils/opening-drag-types';
import {
  openingCenterTs,
  projectOpeningPlacement
} from 'utils/opening-position';
import { OpeningPlacementType } from 'utils/opening-position-types';

export const OPENING_SLIDE_MARGIN_PX = 32;

export const OPENING_PRESETS: Record<OpeningDragType, OpeningPresetType> = {
  'new-window-small': { widthFt: 3, heightFt: 3 },
  'new-window-medium': { widthFt: 4, heightFt: 4 },
  'new-window-large': { widthFt: 6, heightFt: 4 },
  'new-door-wood': { widthFt: 3, heightFt: 7 },
  'new-door-metal': { widthFt: 3, heightFt: 7 },
  'new-door-pet': { widthFt: 2, heightFt: 2 },
  'new-door-glass-single': { widthFt: 3, heightFt: 7 },
  'new-door-glass-double': { widthFt: 8, heightFt: 7 },
  'new-door-garage-single': { widthFt: 9, heightFt: 7 },
  'new-door-garage-double': { widthFt: 16, heightFt: 7 }
};

export function isOpeningDragType(
  type: string | undefined
): type is OpeningDragType {
  return (
    typeof type === 'string' &&
    Object.prototype.hasOwnProperty.call(OPENING_PRESETS, type)
  );
}

export function edgeItemTypeForOpeningDragType(
  type: string | undefined
): EdgeItemType['type'] | null {
  if (!isOpeningDragType(type)) {
    return null;
  }
  if (type.startsWith('new-window-')) {
    return 'window';
  }
  if (type === 'new-door-glass-single' || type === 'new-door-glass-double') {
    return 'glass-door';
  }
  return 'door';
}

export function defaultOpeningWidthFt(type: EdgeItemType['type']): number {
  if (type === 'glass-door') {
    return OPENING_PRESETS['new-door-glass-single'].widthFt;
  }
  if (type === 'door') {
    return OPENING_PRESETS['new-door-wood'].widthFt;
  }
  return OPENING_PRESETS['new-window-small'].widthFt;
}

export function openingWidthForDragType(
  type: string | undefined,
  item?: Pick<EdgeItemType, 'type' | 'width'>
): number {
  if (isOpeningDragType(type)) {
    return OPENING_PRESETS[type].widthFt;
  }
  if (item?.width) {
    return item.width;
  }
  if (item?.type) {
    return defaultOpeningWidthFt(item.type);
  }
  return OPENING_PRESETS['new-window-small'].widthFt;
}

export function sidebarOpeningDropPreview(
  type: string | undefined
): { itemType: EdgeItemType['type']; widthFt: number } | null {
  const itemType = edgeItemTypeForOpeningDragType(type);
  if (!itemType) {
    return null;
  }
  return {
    itemType,
    widthFt: openingWidthForDragType(type)
  };
}

export function openingDragTypeForWindowSize(
  windowType: OpeningWindowSizeType
): OpeningDragType {
  return `new-window-${windowType}`;
}

export function openingDragTypeForDoor({
  doorType = 'solid',
  material,
  garageSize = 'single'
}: {
  doorType?: OpeningDoorKindType;
  material?: 'Wood' | 'Metal';
  garageSize?: OpeningGarageSizeType;
}): OpeningDragType {
  if (doorType === 'pet') {
    return 'new-door-pet';
  }
  if (doorType === 'garage') {
    return garageSize === 'double'
      ? 'new-door-garage-double'
      : 'new-door-garage-single';
  }
  return material === 'Metal' ? 'new-door-metal' : 'new-door-wood';
}

export function openingPresetForWindowType(
  windowType: OpeningWindowSizeType
): OpeningPresetType {
  return OPENING_PRESETS[openingDragTypeForWindowSize(windowType)];
}

export function openingPresetForDoor(params: {
  doorType?: OpeningDoorKindType;
  material?: 'Wood' | 'Metal';
  garageSize?: OpeningGarageSizeType;
}): OpeningPresetType {
  return OPENING_PRESETS[openingDragTypeForDoor(params)];
}

export function edgeItemOpeningTs(
  items: Array<Pick<EdgeItemType, 'id' | 'width' | 'type' | 'positionOnWall'>>,
  wallLengthFt: number
): number[] {
  return openingCenterTs(
    items.map((item) => ({
      id: item.id,
      widthFt: item.width ?? defaultOpeningWidthFt(item.type),
      positionOnWall: item.positionOnWall
    })),
    wallLengthFt
  );
}

export function pointerToSpaceFeet(
  clientX: number,
  clientY: number,
  spaceRect: DOMRect,
  spaceWidthFt: number,
  spaceHeightFt: number
): { x: number; y: number } {
  const width = spaceRect.width || 1;
  const height = spaceRect.height || 1;
  return {
    x: ((clientX - spaceRect.left) / width) * spaceWidthFt,
    y: ((clientY - spaceRect.top) / height) * spaceHeightFt
  };
}

export function isPointerNearSpace(
  clientX: number,
  clientY: number,
  spaceRect: DOMRect,
  marginPx: number = OPENING_SLIDE_MARGIN_PX
): boolean {
  return (
    clientX >= spaceRect.left - marginPx &&
    clientX <= spaceRect.right + marginPx &&
    clientY >= spaceRect.top - marginPx &&
    clientY <= spaceRect.bottom + marginPx
  );
}

export function computeOpeningDragPlacement({
  clientX,
  clientY,
  spaceRect,
  runs,
  widthFt,
  spaceWidthFt,
  spaceHeightFt
}: ComputeOpeningDragPlacementInput): OpeningPlacementType | null {
  if (!isPointerNearSpace(clientX, clientY, spaceRect)) {
    return null;
  }

  const point = pointerToSpaceFeet(
    clientX,
    clientY,
    spaceRect,
    spaceWidthFt,
    spaceHeightFt
  );
  return projectOpeningPlacement(point, runs, widthFt);
}
